import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useLanguageData } from '../../hooks/useLanguageData';
import "../../CSS/ArticleDetail.css";
import SafeHTML from "../SafeHTML";

const ArticleDetail = () => {
    const { id, slug: slugParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [article, setArticle] = useState(null);
    const [recentArticles, setRecentArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const contentRef = useRef(null);
    
    // 🔥 AJOUT: État pour forcer le rechargement des images
    const [imageKey, setImageKey] = useState(0);

    // Hook pour charger tous les articles
    const { 
        data: articlesResponse, 
        loading: articlesLoading 
    } = useLanguageData('/api/v1/embassy/articles?page=1&limit=100');

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });
    };

    // Fonction pour remonter en haut de la page
    const scrollToTop = (behavior = 'instant') => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: behavior
        });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    };

    // Charger les articles récents
    useEffect(() => {
        if (articlesResponse?.data) {
            let foundArticle = null;
            
            if (id) {
                foundArticle = articlesResponse.data.find(a => String(a.id) === String(id));
            }
            
            if (!foundArticle && slugParam) {
                foundArticle = articlesResponse.data.find(a => a.slug === slugParam);
            }
            
            if (foundArticle) {
                setArticle(foundArticle);
                const otherArticles = articlesResponse.data.filter(a => a.id !== foundArticle.id);
                setRecentArticles(otherArticles);
                // 🔥 Forcer le rechargement des images quand l'article change
                setImageKey(prev => prev + 1);
            } else {
                setError(t('articleDetail.notFound'));
            }
            
            setLoading(false);
        }
    }, [articlesResponse, id, slugParam, t]);

    useEffect(() => {
        if (location.state?.articleData) {
            setArticle(location.state.articleData);
            setLoading(false);
            
            if (location.state.recentArticles) {
                setRecentArticles(location.state.recentArticles);
            }
            // 🔥 Forcer le rechargement des images
            setImageKey(prev => prev + 1);
        }
    }, [location.state]);

    // Gestion du scroll après le chargement complet
    useEffect(() => {
        if (article && !articlesLoading && !loading) {
            requestAnimationFrame(() => {
                scrollToTop('instant');
            });
        }
    }, [article, articlesLoading, loading]);

    // Scroll à chaque changement d'URL
    useEffect(() => {
        scrollToTop('instant');
    }, [id, slugParam]);

    // Gestionnaire de clic pour les articles récents
    const handleRecentArticleClick = (recentArticle) => {
        scrollToTop('instant');
        
        setTimeout(() => {
            navigate(`/article/${recentArticle.id}/${recentArticle.slug}`, {
                state: { 
                    articleData: recentArticle,
                    recentArticles: recentArticles
                }
            });
        }, 10);
    };

    // 🔥 Nouvelle fonction pour gérer les erreurs d'image
    const handleImageError = (e) => {
        console.warn('Image non chargée');
        e.target.style.display = 'none';
        // Optionnel: Ajouter une image par défaut
        // e.target.src = '/images/default-image.jpg';
    };

    // Composant de spinner de chargement
    const LoadingSpinner = () => (
        <div className="loading">
            <div style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #1a3c5e",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 1rem"
            }}></div>
            <p>{t('articleDetail.loading')}</p>
            
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );

    if (articlesLoading || loading) {
        return <LoadingSpinner />;
    }

    if (error || !article) {
        return (
            <div className="error">
                <h2>{t('articleDetail.notFound')}</h2>
                <p>{error || t('articleDetail.notFoundMessage')}</p>
                <Link to="/articles" className="back-link">← {t('articleDetail.backToArticles')}</Link>
            </div>
        );
    }

    return (
        <div className="article-detail-container" ref={contentRef}>
            {/* Fil d'Ariane */}
            <nav className="breadcrumb">
                <Link to="/" style={{textDecoration:"none", color:"#878787ff"}}>{t('breadcrumb.home')}</Link>
                <span> / </span>
                <Link to="/articles" style={{textDecoration:"none", color:"#878787ff"}}>{t('breadcrumb.news')}</Link>
                <span> / </span>
                <span className="current" style={{color:'#0a3764'}}>{article.articleCategory}</span>
            </nav>

            {/* Catégorie et date */}
            <div className="article-meta">
                <span className="article-date">
                   {article.articleCategory} | {formatDate(article.date.date)}
                </span>
            </div>

            <div className="article-detail-content">
                <div className="article-layout" style={{ textAlign: 'left' }}>
                    {/* Colonne de gauche - Détail de l'article */}
                    <div className="">
                        <article className="article-full">
                            {/* Titre */}
                            <h1 className="article-title" style={{ lineHeight: "40px" }}>{article.title}</h1>
                            <span style={{ fontWeight: '400' }}>
                                {t('articleDetail.publishedOn')} {new Date(article.date.date).toLocaleDateString('fr-FR')}
                            </span>

                            {/* 🔥 Image principale avec key pour forcer le rechargement */}
                            {article.banner ? (
                                <div className="article-banner mt-4">
                                    <img
                                        key={`main-${article.id}-${imageKey}`}
                                        src={article.banner.replace(/([^:]\/)\/+/g, "$1")}
                                        alt={article.slug}
                                        loading="lazy"
                                        onError={handleImageError}
                                    />
                                </div>
                            ) : (
                                /* 🔥 Afficher un placeholder si pas d'image */
                                <div className="article-banner mt-4 no-image-placeholder">
                                    <div style={{
                                        backgroundColor: '#f5f5f5',
                                        height: '300px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#999'
                                    }}>
                                        {t('articleDetail.noImage') || 'Aucune image disponible'}
                                    </div>
                                </div>
                            )}

                            {/* Contenu de l'article */}
                            <div className="article-content">
                                {article.content ? (
                                    <div className="article-html-content">
                                        <SafeHTML html={article.content} />
                                    </div>
                                ) : article.url ? (
                                    <div className="external-link-content">
                                        <p>{t('articleDetail.externalLinkMessage')}</p>
                                        <a
                                            href={article.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="external-link-button"
                                        >
                                            📖 {t('articleDetail.readOnLinkedIn')}
                                        </a>
                                        <p className="link-url">
                                            <small>{article.url}</small>
                                        </p>
                                    </div>
                                ) : (
                                    <div className="no-content">
                                        <p>{t('articleDetail.noContent')}</p>
                                        {article.description && (
                                            <div className="description-fallback">
                                                <h3>{t('articleDetail.description')} :</h3>
                                                <p>{article.description}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </article>
                    </div>

                    {/* Colonne de droite - Articles récents */}
                    <div className="recent-articles-sidebar">
                        <h3 className="sidebar-title">{t('articleDetail.recentArticles')}</h3>
                        <div className="recent-articles-list">
                            {recentArticles
                                .filter(recentArticle => recentArticle.slug !== article.slug)
                                .slice(0, 3)
                                .map((recentArticle) => (
                                    <div
                                        key={recentArticle.id}
                                        className="recent-article-item"
                                        onClick={() => handleRecentArticleClick(recentArticle)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="recent-article-content">
                                            <span className="recent-article-category">
                                                {recentArticle.articleCategory} | {formatDate(recentArticle.date.date)}
                                            </span>
                                            <h4 className="recent-article-title">
                                                {recentArticle.title}
                                            </h4>
                                        </div>
                                        <a className="read-article-btn">{t('articleDetail.readArticle')}</a>
                                        {/* 🔥 Images des articles récents avec key */}
                                        {recentArticle.banner && (
                                            <div className="recent-article-image">
                                                <img
                                                    key={`recent-${recentArticle.id}-${imageKey}`}
                                                    src={recentArticle.banner.replace(/([^:]\/)\/+/g, "$1")}
                                                    alt={recentArticle.slug}
                                                    loading="lazy"
                                                    onError={handleImageError}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))
                            }

                            {recentArticles.length === 0 && (
                                <p className="no-recent-articles">{t('articleDetail.noRecentArticles')}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArticleDetail;